import { sleep } from "@/lib/utils";

import { ApiError } from "../errors";
import type {
	Api,
	SellerDocument,
	SellerManagerContact,
	SellerShop,
	SellerShopDetail,
	ShopMember,
	StaffAssignableRole,
	User,
} from "../types";

const MOCK_ADMIN_MANAGER: SellerManagerContact = {
	id: "usr_demo_admin",
	name: "Платформа",
	email: "admin@osmix.ru",
	phone: "+7 (999) 000-00-00",
};

const staffMembers: ShopMember[] = [
	{
		id: "shm_demo_owner",
		shopId: "shop-handmade",
		userId: "usr_demo_anna",
		name: "Анна Мастерова",
		email: "anna@osmix.ru",
		phone: "+7 (999) 123-45-67",
		role: "shop-owner",
		status: "active",
		invitedAt: "2026-01-15T09:00:00Z",
		joinedAt: "2026-01-15T09:00:00Z",
	},
	{
		id: "shm_demo_order_manager",
		shopId: "shop-handmade",
		userId: "usr_demo_order_manager",
		name: "Ольга Заказова",
		email: "orders@osmix.ru",
		phone: "+7 (999) 555-44-33",
		role: "shop-order-manager",
		status: "active",
		invitedAt: "2026-02-01T09:00:00Z",
		joinedAt: "2026-02-01T09:10:00Z",
	},
];

// Mirrors the gateway's seller-shop moderation (apps/api-gateway/src/services/
// sellerShops.ts): each shop is a seller plus the onboarding submission they
// filled in. Mutated in place by approve/reject/block so the offline path
// behaves like the real one.
let fixtures: SellerShopDetail[] = [
	{
		id: "usr_seed_pending",
		shopId: "shop-keramika",
		shopName: "Гончарная мастерская",
		sellerName: "Иван Гончаров",
		email: "ivan-pottery@osmix.ru",
		phone: "+7 (911) 222-33-44",
		role: "seller",
		status: "on_moderation",
		onboardingStatus: "moderation_pending",
		submittedAt: "2026-05-20T11:35:00.000Z",
		createdAt: "2026-05-20T10:00:00.000Z",
		manager: null,
		teamLead: null,
		survey: {
			businessForm: "self_employed",
			role: "manufacturer",
			categories: ["home", "kitchen"],
			salesChannels: ["instagram", "markets"],
			contact: {
				name: "Иван Гончаров",
				city: "Москва",
				phone: "+7 (911) 222-33-44",
				preferredContact: "phone",
				preferredTimeSlots: ["12:00–15:00"],
			},
		},
		shop: {
			shopName: "Гончарная мастерская",
			foundingDate: "2022",
			brandDescription:
				"Керамическая посуда ручной лепки: тарелки, чашки и вазы. Каждое изделие обжигается и расписывается вручную.",
			mainProduct: "Керамическая посуда",
			role: "manufacturer",
			roleCustom: "",
			categories: ["home", "kitchen"],
			salesChannels: ["instagram", "markets"],
			salesChannelsCustom: "Ярмарки выходного дня",
			socials: { instagram: "@goncharnaya_masterskaya" },
		},
		requisites: {
			inn: "770987654321",
			shortName: "Гончаров И. П. (НПД)",
			fullName: "Гончаров Иван Петрович",
			organizationForm: "self_employed",
			legalAddress: "г. Москва, ул. Гончарная, д. 7, кв. 18",
			bankAccount: "40817810099910004321",
			bik: "044525225",
			accountantContact: "+7 (911) 222-33-44",
		},
		documents: [
			{
				id: "doc-1",
				name: "Справка о постановке на учёт (самозанятый)",
				required: true,
				status: "uploaded",
				fileName: "npd_spravka.pdf",
				uploadedAt: "2026-05-20T11:30:00.000Z",
			},
			{
				id: "doc-2",
				name: "Паспорт (основные страницы)",
				required: true,
				status: "uploaded",
				fileName: "passport_ivan.pdf",
				uploadedAt: "2026-05-20T11:34:00.000Z",
			},
		],
		founders: [
			{
				id: "f-1",
				fullName: "Гончаров Иван Петрович",
				position: "Самозанятый",
				phone: "+7 (911) 222-33-44",
				email: "ivan-pottery@osmix.ru",
			},
		],
	},
	{
		id: "usr_demo_anna",
		shopId: "shop-handmade",
		shopName: "Мастерская Анны",
		sellerName: "Анна Мастерова",
		email: "seller@osmix.ru",
		phone: "+7 (999) 123-45-67",
		role: "verified_seller",
		status: "active",
		onboardingStatus: "active",
		submittedAt: "2024-11-02T09:10:00.000Z",
		createdAt: "2024-10-30T08:00:00.000Z",
		manager: MOCK_ADMIN_MANAGER,
		teamLead: null,
		survey: {
			businessForm: "individual_entrepreneur",
			role: "manufacturer",
			categories: ["jewelry", "accessories"],
			salesChannels: ["own_website", "instagram"],
			contact: {
				name: "Анна Мастерова",
				city: "Санкт-Петербург",
				phone: "+7 (999) 123-45-67",
				preferredContact: "telegram",
				preferredTimeSlots: ["10:00–12:00", "15:00–18:00"],
			},
		},
		shop: {
			shopName: "Мастерская Анны",
			foundingDate: "2019",
			brandDescription:
				"Украшения ручной работы из серебра 925 пробы и натуральных камней.",
			mainProduct: "Серьги, подвески, браслеты",
			role: "manufacturer",
			roleCustom: "",
			categories: ["jewelry", "accessories"],
			salesChannels: ["own_website", "instagram"],
			salesChannelsCustom: "",
			socials: {
				website: "https://masterskaya-anny.ru",
				instagram: "@masterskaya_anny",
			},
		},
		requisites: {
			inn: "781234567890",
			shortName: "ИП Мастерова А. С.",
			fullName: "Индивидуальный предприниматель Мастерова Анна Сергеевна",
			organizationForm: "individual_entrepreneur",
			legalAddress: "г. Санкт-Петербург, ул. Рубинштейна, д. 12, кв. 5",
			bankAccount: "40802810500000012345",
			bik: "044030790",
			ogrn: "320784700123456",
			accountantContact: "+7 (999) 123-45-67",
		},
		documents: [
			{
				id: "doc-1",
				name: "Свидетельство о регистрации ИП",
				required: true,
				status: "uploaded",
				fileName: "ip_certificate.pdf",
				uploadedAt: "2024-11-02T09:00:00.000Z",
			},
			{
				id: "doc-2",
				name: "Паспорт (основные страницы)",
				required: true,
				status: "uploaded",
				fileName: "passport.pdf",
				uploadedAt: "2024-11-02T09:05:00.000Z",
			},
		],
		founders: [
			{
				id: "f-1",
				fullName: "Мастерова Анна Сергеевна",
				position: "Основатель",
				phone: "+7 (999) 123-45-67",
				email: "anna@osmix.ru",
			},
		],
	},
];

// Owners for the rest of the storefront catalog, mirroring the gateway seed
// (api-gateway/src/data/sellers.ts) so every storefront resolves a seller
// offline too — with a spread of statuses. Иван (above) owns shop-keramika and
// Анна owns shop-handmade; these six cover the remaining storefronts.
type OwnerStatus = "active" | "on_moderation" | "blocked" | "filling";

const STATUS_ONBOARDING: Record<
	OwnerStatus,
	{ role: User["role"]; onboardingStatus: string }
> = {
	active: { role: "verified_seller", onboardingStatus: "active" },
	on_moderation: { role: "seller", onboardingStatus: "moderation_pending" },
	blocked: { role: "verified_seller", onboardingStatus: "active" },
	filling: { role: "seller", onboardingStatus: "profile_pending" },
};

type OwnerSpec = {
	userId: string;
	shopId: string;
	slug: string;
	name: string;
	description: string;
	since: string;
	city: string;
	owner: { name: string; email: string; phone: string };
	status: OwnerStatus;
	businessForm: "legal_entity" | "self_employed" | "individual_entrepreneur";
	categories: string[];
	salesChannels: string[];
	mainProduct: string;
	requisites: Omit<
		SellerShopDetail["requisites"],
		"organizationForm" | "accountantContact"
	>;
};

const OWNER_SPECS: OwnerSpec[] = [
	{
		userId: "usr_seed_silver_moon",
		shopId: "shop-silver-moon",
		slug: "silver-moon",
		name: "Silver Moon",
		description:
			"Небольшая ювелирная мастерская. Делаем серьги, подвески и браслеты вручную из серебра 925 пробы и натуральных камней.",
		since: "2019",
		city: "Санкт-Петербург",
		owner: {
			name: "Елена Серебрякова",
			email: "silver-moon@osmix.ru",
			phone: "+7 (812) 244-18-90",
		},
		status: "active",
		businessForm: "legal_entity",
		categories: ["accessories"],
		salesChannels: ["ownStore", "social"],
		mainProduct: "Серьги, подвески, браслеты из серебра",
		requisites: {
			inn: "7801234561",
			shortName: 'ООО "Сильвер Мун"',
			fullName: 'Общество с ограниченной ответственностью "Сильвер Мун"',
			legalAddress: "г. Санкт-Петербург, ул. Марата, д. 18, лит. А, пом. 3",
			bankAccount: "40702810800000056781",
			bik: "044030790",
			kpp: "780101001",
			ogrn: "1197847012345",
		},
	},
	{
		userId: "usr_seed_natur",
		shopId: "shop-natur",
		slug: "natur-tekstil",
		name: "НатурТекстиль",
		description:
			"Шьём и вяжем из льна, хлопка и мериносовой шерсти. Спокойные силуэты, природные оттенки, долгая носка.",
		since: "2015",
		city: "Казань",
		owner: {
			name: "Гузель Хайруллина",
			email: "natur-tekstil@osmix.ru",
			phone: "+7 (843) 512-77-21",
		},
		status: "active",
		businessForm: "individual_entrepreneur",
		categories: ["womenClothing", "home"],
		salesChannels: ["ownStore", "marketplaces"],
		mainProduct: "Одежда и текстиль из льна и хлопка",
		requisites: {
			inn: "165912345678",
			shortName: "ИП Хайруллина Г. Р.",
			fullName: "Индивидуальный предприниматель Хайруллина Гузель Рашидовна",
			legalAddress: "г. Казань, ул. Баумана, д. 44, кв. 12",
			bankAccount: "40802810300000067892",
			bik: "049205603",
			ogrn: "315169000045678",
		},
	},
	{
		userId: "usr_seed_artcraft",
		shopId: "shop-artcraft",
		slug: "artcraft",
		name: "ArtCraft",
		description:
			"Мастерская кожевенных изделий. Кошельки, ремни и сумки из кожи растительного дубления с ручным тиснением.",
		since: "2018",
		city: "Екатеринбург",
		owner: {
			name: "Дмитрий Кожевников",
			email: "artcraft@osmix.ru",
			phone: "+7 (343) 388-90-12",
		},
		status: "on_moderation",
		businessForm: "self_employed",
		categories: ["accessories"],
		salesChannels: ["social", "offlineStores"],
		mainProduct: "Кошельки, ремни и сумки из кожи",
		requisites: {
			inn: "667812345609",
			shortName: "Кожевников Д. А. (НПД)",
			fullName: "Кожевников Дмитрий Алексеевич",
			legalAddress: "г. Екатеринбург, ул. Малышева, д. 71, кв. 5",
			bankAccount: "40817810500000078903",
			bik: "046577674",
		},
	},
	{
		userId: "usr_seed_lina",
		shopId: "shop-lina",
		slug: "masterskaya-liny",
		name: "Мастерская Лины",
		description:
			"Авторская мастерская Лины. Льняные платья во французском стиле — шьём небольшими партиями под сезон.",
		since: "2021",
		city: "Сочи",
		owner: {
			name: "Лина Карапетян",
			email: "masterskaya-liny@osmix.ru",
			phone: "+7 (862) 270-55-30",
		},
		status: "on_moderation",
		businessForm: "individual_entrepreneur",
		categories: ["womenClothing"],
		salesChannels: ["ownStore", "social"],
		mainProduct: "Льняные платья ручного пошива",
		requisites: {
			inn: "231512345690",
			shortName: "ИП Карапетян Л. А.",
			fullName: "Индивидуальный предприниматель Карапетян Лина Ашотовна",
			legalAddress: "г. Сочи, ул. Навагинская, д. 9, кв. 21",
			bankAccount: "40802810700000089014",
			bik: "040349602",
			ogrn: "321237500067890",
		},
	},
	{
		userId: "usr_seed_eco",
		shopId: "shop-eco",
		slug: "eco-workshop",
		name: "Eco Workshop",
		description:
			"Свечи из соевого воска, натуральное мыло, восковые салфетки и эфирные масла. Минимум упаковки, максимум пользы.",
		since: "2020",
		city: "Краснодар",
		owner: {
			name: "Ольга Зеленова",
			email: "eco-workshop@osmix.ru",
			phone: "+7 (861) 201-44-88",
		},
		status: "blocked",
		businessForm: "self_employed",
		categories: ["cosmetics", "home"],
		salesChannels: ["marketplaces", "social"],
		mainProduct: "Свечи, мыло и эфирные масла",
		requisites: {
			inn: "230912345671",
			shortName: "Зеленова О. И. (НПД)",
			fullName: "Зеленова Ольга Игоревна",
			legalAddress: "г. Краснодар, ул. Красная, д. 155, кв. 40",
			bankAccount: "40817810600000090125",
			bik: "040349602",
		},
	},
	{
		userId: "usr_seed_uyut",
		shopId: "shop-uyut",
		slug: "uyutny-dom",
		name: "Уютный дом",
		description:
			"Пледы, разделочные доски и предметы интерьера из натуральных материалов — для тёплого и спокойного дома.",
		since: "2016",
		city: "Нижний Новгород",
		owner: {
			name: "Сергей Тёплов",
			email: "uyutny-dom@osmix.ru",
			phone: "+7 (831) 419-62-15",
		},
		status: "filling",
		businessForm: "individual_entrepreneur",
		categories: ["home"],
		salesChannels: ["ownStore"],
		mainProduct: "Пледы, разделочные доски и декор",
		requisites: {
			inn: "526012345682",
			shortName: "ИП Тёплов С. В.",
			fullName: "Индивидуальный предприниматель Тёплов Сергей Викторович",
			legalAddress: "г. Нижний Новгород, ул. Большая Покровская, д. 30, кв. 8",
			bankAccount: "",
			bik: "",
			ogrn: "316527500078901",
		},
	},
];

const buildOwner = (spec: OwnerSpec): SellerShopDetail => {
	const { role, onboardingStatus } = STATUS_ONBOARDING[spec.status];
	const partial = spec.status === "filling";
	return {
		id: spec.userId,
		shopId: spec.shopId,
		shopName: spec.name,
		sellerName: spec.owner.name,
		email: spec.owner.email,
		phone: spec.owner.phone,
		role,
		status: spec.status,
		onboardingStatus,
		submittedAt: "2025-09-01T09:10:00.000Z",
		createdAt: "2025-08-28T08:00:00.000Z",
		manager: null,
		teamLead: null,
		survey: {
			businessForm: spec.businessForm,
			role: "manufacturer",
			categories: spec.categories,
			salesChannels: spec.salesChannels,
			contact: {
				name: spec.owner.name,
				city: spec.city,
				phone: spec.owner.phone,
				preferredContact: "phone",
				preferredTimeSlots: ["12:00-15:00"],
			},
		},
		shop: {
			shopName: spec.name,
			foundingDate: spec.since,
			brandDescription: spec.description,
			mainProduct: spec.mainProduct,
			role: "manufacturer",
			roleCustom: "",
			categories: spec.categories,
			salesChannels: spec.salesChannels,
			salesChannelsCustom: "",
			socials: { instagram: `@${spec.slug.replace(/-/g, "_")}` },
		},
		requisites: {
			...spec.requisites,
			organizationForm: spec.businessForm,
			accountantContact: spec.owner.phone,
		},
		documents: [
			{
				id: "doc-1",
				name: "Свидетельство о регистрации",
				required: true,
				status: "uploaded",
				fileName: "registration.pdf",
				uploadedAt: "2025-09-01T09:00:00.000Z",
			},
			{
				id: "doc-2",
				name: "Паспорт (основные страницы)",
				required: true,
				status: partial ? "pending" : "uploaded",
				...(partial
					? {}
					: {
							fileName: "passport.pdf",
							uploadedAt: "2025-09-01T09:05:00.000Z",
						}),
			},
		],
		founders: [
			{
				id: "f-1",
				fullName: spec.owner.name,
				position:
					spec.businessForm === "legal_entity"
						? "Генеральный директор"
						: "Основатель",
				phone: spec.owner.phone,
				email: spec.owner.email,
			},
		],
	};
};

fixtures = [...fixtures, ...OWNER_SPECS.map(buildOwner)];

// ── Э7: «личное дело» продавца (документы верификации, миграция 044) ──────────
// Офлайн-заглушка, чтобы панель документов не падала без бэкенда. Ключ — userId
// продавца. Мутируется reviewDocument, как это делает реальный сервис.
const sellerDocuments: Record<string, SellerDocument[]> = {
	// Продавец на модерации — документы ждут проверки.
	usr_seed_pending: [
		{
			id: "sdoc_pending_passport",
			docType: "passport_ip",
			filename: "passport-ip.pdf",
			mime: "application/pdf",
			sizeBytes: 482_310,
			status: "uploaded",
			createdAt: "2026-05-20T11:31:00.000Z",
			fileUrl: "/api/v1/seller/documents/sdoc_pending_passport/file",
		},
		{
			id: "sdoc_pending_tax",
			docType: "tax_system",
			filename: "npd-spravka.pdf",
			mime: "application/pdf",
			sizeBytes: 128_940,
			status: "uploaded",
			createdAt: "2026-05-20T11:33:00.000Z",
			fileUrl: "/api/v1/seller/documents/sdoc_pending_tax/file",
		},
	],
	// Действующий продавец — документы уже приняты (бейдж «Проверенный»).
	usr_demo_anna: [
		{
			id: "sdoc_anna_passport",
			docType: "passport_ip",
			filename: "passport.jpg",
			mime: "image/jpeg",
			sizeBytes: 1_204_500,
			status: "accepted",
			reviewNote: "Документ читаем, данные совпадают с реквизитами.",
			reviewedAt: "2024-11-03T10:00:00.000Z",
			createdAt: "2024-11-02T09:01:00.000Z",
			fileUrl: "/api/v1/seller/documents/sdoc_anna_passport/file",
		},
		{
			id: "sdoc_anna_tax",
			docType: "tax_system",
			filename: "usn-uvedomlenie.pdf",
			mime: "application/pdf",
			sizeBytes: 210_770,
			status: "accepted",
			reviewedAt: "2024-11-03T10:02:00.000Z",
			createdAt: "2024-11-02T09:03:00.000Z",
			fileUrl: "/api/v1/seller/documents/sdoc_anna_tax/file",
		},
	],
	// Продавец на модерации с отклонённым документом — виден статус + примечание.
	usr_seed_artcraft: [
		{
			id: "sdoc_artcraft_passport",
			docType: "passport_ip",
			filename: "passport-scan.png",
			mime: "image/png",
			sizeBytes: 890_120,
			status: "rejected",
			reviewNote: "Скан обрезан — не видно страницу с пропиской. Загрузите заново.",
			reviewedAt: "2025-09-02T12:00:00.000Z",
			createdAt: "2025-09-01T09:02:00.000Z",
			fileUrl: "/api/v1/seller/documents/sdoc_artcraft_passport/file",
		},
		{
			id: "sdoc_artcraft_authority",
			docType: "authority_ul",
			filename: "doverennost.pdf",
			mime: "application/pdf",
			sizeBytes: 356_400,
			status: "uploaded",
			createdAt: "2025-09-01T09:04:00.000Z",
			fileUrl: "/api/v1/seller/documents/sdoc_artcraft_authority/file",
		},
	],
};

// Продавец «проверен», если он активен и хотя бы один документ принят —
// упрощённая офлайн-версия recomputeVerification из бэкенда.
const isVerified = (s: SellerShopDetail): boolean =>
	s.status === "active" &&
	(sellerDocuments[s.id] ?? []).some((d) => d.status === "accepted");

const findDocOwner = (docId: string): { userId: string; doc: SellerDocument } | null => {
	for (const [userId, docs] of Object.entries(sellerDocuments)) {
		const doc = docs.find((d) => d.id === docId);
		if (doc) return { userId, doc };
	}
	return null;
};

// Owner user stubs so mock/users.ts resolves a seller for every storefront —
// the same User.shopId join the storefront detail page does in the wired app.
// Анна already lives in mock/users.ts; Иван and the six specs are added here.
export const STOREFRONT_OWNER_USERS: User[] = [
	{
		id: "usr_seed_pending",
		email: "ivan-pottery@osmix.ru",
		phone: "+7 (911) 222-33-44",
		name: "Иван Гончаров",
		shopName: "Гончарная мастерская",
		shopId: "shop-keramika",
		role: "seller",
		onboardingStatus: "moderation_pending",
		createdAt: "2026-05-20T10:00:00.000Z",
	},
	...OWNER_SPECS.map((s) => ({
		id: s.userId,
		email: s.owner.email,
		phone: s.owner.phone,
		name: s.owner.name,
		shopName: s.name,
		shopId: s.shopId,
		role: STATUS_ONBOARDING[s.status].role,
		onboardingStatus: STATUS_ONBOARDING[s.status].onboardingStatus,
		createdAt: "2025-08-28T08:00:00.000Z",
	})),
];

const find = (id: string): SellerShopDetail => {
	const shop = fixtures.find((s) => s.id === id);
	if (!shop) throw new ApiError("Seller shop not found", 404, "NOT_FOUND");
	return shop;
};

const summary = (s: SellerShopDetail): SellerShop => ({
	id: s.id,
	shopId: s.shopId,
	shopName: s.shopName,
	sellerName: s.sellerName,
	email: s.email,
	phone: s.phone,
	role: s.role,
	status: s.status,
	onboardingStatus: s.onboardingStatus,
	submittedAt: s.submittedAt,
	rejectionReason: s.rejectionReason,
	createdAt: s.createdAt,
	manager: s.manager,
	verified: isVerified(s),
});

const patch = (id: string, next: Partial<SellerShopDetail>): SellerShop => {
	const updated = { ...find(id), ...next };
	fixtures = fixtures.map((s) => (s.id === id ? updated : s));
	return summary(updated);
};

export const sellers: Api["sellers"] = {
	async list() {
		await sleep(180);
		// Mirror the gateway ordering: shops awaiting moderation float to the top,
		// then newest first.
		return [...fixtures]
			.sort((a, b) => {
				const pa = a.onboardingStatus === "moderation_pending" ? 0 : 1;
				const pb = b.onboardingStatus === "moderation_pending" ? 0 : 1;
				if (pa !== pb) return pa - pb;
				return b.createdAt.localeCompare(a.createdAt);
			})
			.map(summary);
	},
	async get(id) {
		await sleep(150);
		return find(id);
	},
	async approve(id) {
		await sleep(250);
		return patch(id, {
			role: "verified_seller",
			status: "active",
			onboardingStatus: "moderation_passed",
			rejectionReason: undefined,
		});
	},
	async reject(id, reason, _opts) {
		await sleep(250);
		const cleanReason = reason?.trim();
		if (!cleanReason) throw new ApiError("Rejection reason required", 400, "VALIDATION_ERROR");
		return patch(id, {
			status: "filling",
			onboardingStatus: "profile_pending",
			rejectionReason: cleanReason,
		});
	},
	async block(id) {
		await sleep(250);
		return patch(id, { status: "blocked" });
	},
	async unblock(id) {
		await sleep(250);
		return patch(id, { status: "active" });
	},
	async assignManager(id, managerUserId) {
		await sleep(250);
		if (managerUserId !== MOCK_ADMIN_MANAGER.id) {
			throw new ApiError("Manager must be an admin user", 400, "VALIDATION_ERROR");
		}
		patch(id, { manager: MOCK_ADMIN_MANAGER });
		return find(id);
	},
	async clearManager(id) {
		await sleep(250);
		patch(id, { manager: null });
		return find(id);
	},
	// Тимлид приходит по shopKey (shopId ?? userId), поэтому ищем запись и по
	// витрине, и по id продавца — так же, как ключуется бэкенд.
	async assignTeamLead(shopKey, leadUserId) {
		await sleep(250);
		const shop = fixtures.find((s) => s.id === shopKey || s.shopId === shopKey);
		if (!shop) throw new ApiError("Seller shop not found", 404, "NOT_FOUND");
		shop.teamLead = {
			userId: leadUserId,
			name: leadUserId,
			email: "",
			assignedAt: new Date().toISOString(),
		};
	},
	async clearTeamLead(shopKey) {
		await sleep(250);
		const shop = fixtures.find((s) => s.id === shopKey || s.shopId === shopKey);
		if (!shop) throw new ApiError("Seller shop not found", 404, "NOT_FOUND");
		shop.teamLead = null;
	},
	async staff(id) {
		await sleep(150);
		const seller = find(id);
		const shopId = seller.shopId ?? id;
		return {
			members: staffMembers.filter((member) => member.shopId === shopId && member.status === "active"),
			invites: [],
		};
	},
	async updateStaffRole(id, memberId, role: StaffAssignableRole) {
		await sleep(250);
		const seller = find(id);
		const shopId = seller.shopId ?? id;
		const member = staffMembers.find((item) => item.id === memberId && item.shopId === shopId);
		if (!member) throw new ApiError("Staff member not found", 404, "NOT_FOUND");
		if (member.role === "shop-owner") throw new ApiError("Owner role cannot be changed", 400, "VALIDATION_ERROR");
		member.role = role;
		return member;
	},
	async removeStaffMember(id, memberId) {
		await sleep(250);
		const seller = find(id);
		const shopId = seller.shopId ?? id;
		const member = staffMembers.find((item) => item.id === memberId && item.shopId === shopId);
		if (!member) throw new ApiError("Staff member not found", 404, "NOT_FOUND");
		if (member.role === "shop-owner") throw new ApiError("Owner cannot be removed", 400, "VALIDATION_ERROR");
		member.status = "removed";
		member.removedAt = new Date().toISOString();
	},
	async profileDocumentFile(id, docId) {
		await sleep(150);
		const doc = find(id).documents.find((d) => d.id === docId);
		if (!doc) throw new ApiError("Document file not found", 404);
		// Offline placeholder — real bytes live in the gateway's private bucket.
		// Enough to exercise the viewer's open-in-tab / download wiring.
		return new Blob([`Mock document: ${doc.fileName ?? doc.name}`], {
			type: "text/plain",
		});
	},
	// ── Э7: «личное дело» ─────────────────────────────────────────────────────
	async documents(id) {
		await sleep(150);
		find(id); // 404, если продавца нет
		return { documents: [...(sellerDocuments[id] ?? [])] };
	},
	async reviewDocument(docId, input) {
		await sleep(200);
		const owned = findDocOwner(docId);
		if (!owned) throw new ApiError("Document not found", 404, "NOT_FOUND");
		if (input.status === "rejected" && !input.note?.trim()) {
			// В UI примечание к отклонению обязательно; на всякий случай и здесь.
			throw new ApiError("Review note required for rejection", 400, "VALIDATION_ERROR");
		}
		const next: SellerDocument = {
			...owned.doc,
			status: input.status,
			reviewNote: input.note?.trim() || undefined,
			reviewedAt: new Date().toISOString(),
		};
		sellerDocuments[owned.userId] = (sellerDocuments[owned.userId] ?? []).map((d) =>
			d.id === docId ? next : d,
		);
		return next;
	},
	async documentFile(id, docId) {
		await sleep(150);
		const doc = (sellerDocuments[id] ?? []).find((d) => d.id === docId);
		if (!doc) throw new ApiError("Document file not found", 404);
		// Офлайн-заглушка байтов: реальные лежат в приватном бакете шлюза. Тип
		// сохраняем из документа, чтобы вьюер выбрал корректный режим показа.
		return new Blob([`Mock seller document: ${doc.filename}`], {
			type: doc.mime,
		});
	},
	async verification(id) {
		await sleep(150);
		find(id); // 404, если продавца нет
		// Офлайн-заглушка: сводим чек-лист из загруженных документов. Реальное
		// состояние (в т.ч. авто-проверку ИНН) отдаёт бэкенд.
		const docs = sellerDocuments[id] ?? [];
		const required = ["passport_ip", "tax_system", "authority_ul"] as const;
		const checklist = required.map((docType) => {
			const doc = docs.find((d) => d.docType === docType);
			return {
				docType,
				status: doc ? doc.status : ("missing" as const),
			};
		});
		const documentsAccepted = docs.some((d) => d.status === "accepted");
		return {
			inn: null,
			documentsAccepted,
			checklist,
			verified: false,
			verifiedAt: null,
		};
	},
	// П7: разделы анкеты. Офлайн-заглушка: статус по стадии продавца.
	async sections(id) {
		await sleep(100);
		const seller = find(id);
		const status = seller.status === "active" ? "accepted" : seller.status === "on_moderation" ? "submitted" : "draft";
		return (mockSections[id] ??= (["about", "requisites", "documents"] as const).map((section) => ({
			section,
			status,
			changedAfterReview: false,
		})));
	},
	async reviewSection(id, section, input) {
		await sleep(100);
		const list = await this.sections(id);
		mockSections[id] = list.map((s) =>
			s.section === section
				? {
						...s,
						status: input.status,
						remark: input.status === "rework" ? input.remark : undefined,
						reviewedAt: new Date().toISOString(),
						changedAfterReview: false,
					}
				: s,
		);
		return mockSections[id];
	},
};

const mockSections: Record<string, import("../types").ProfileSectionState[]> = {};
